// Pure helpers for the Research Pulse admin pages (no React).

import { knownLifeAreas } from "../../Explore/lifeAreas";

export {
  errorMessage,
  formatCount,
  formatDateTime,
} from "../ResearchLibrary/researchLibraryFormat";

/** Every triage status, in workflow order, with its badge and meaning. */
export const STATUS_META = {
  NEW: { label: "New", variant: "info", hint: "Not triaged yet" },
  RESEARCH: {
    label: "Research",
    variant: "default",
    hint: "Queued for Science & Research review",
  },
  WATCH: { label: "Watch", variant: "secondary", hint: "Keep an eye on the trend" },
  COVERED_EXISTING: {
    label: "Covered by existing",
    variant: "success",
    hint: "A canonical Dialogue already covers this need",
  },
  RETRIEVAL_BUG: {
    label: "Retrieval bug",
    variant: "destructive",
    hint: "A suitable Dialogue exists but wasn't found — route to product / QA",
  },
  DUPLICATE: { label: "Duplicate", variant: "outline", hint: "Same need as another cluster" },
  OUT_OF_SCOPE: {
    label: "Out of scope",
    variant: "outline",
    hint: "Not something the Dialogue Library should cover",
  },
  REJECTED: { label: "Rejected", variant: "outline", hint: "Not a real need" },
};

export const STATUSES = Object.keys(STATUS_META);

export const statusMeta = (status) =>
  STATUS_META[String(status || "").toUpperCase()] || {
    label: status || "—",
    variant: "outline",
    hint: "",
  };

export const isRetrievalBug = (row) =>
  String(row?.status || "").toUpperCase() === "RETRIEVAL_BUG";

/** The research-to-release traceability stages, in order. */
export const TRACE_STAGES = [
  { value: "research_review", label: "Research review" },
  { value: "authored_dialogue_id", label: "Authored Dialogue" },
  { value: "forensic_qa", label: "Forensic QA" },
  { value: "certification", label: "Certification" },
  { value: "release_version", label: "Release version" },
];

export const traceStageLabel = (stage) =>
  TRACE_STAGES.find((s) => s.value === stage)?.label || stage;

export const lifeAreaLabel = (key) =>
  knownLifeAreas().find((area) => area.key === key)?.title || key || "—";

/**
 * Life areas a Coverage Signal can belong to. Self-Knowledge is served by Aris
 * Elements, never by the Dialogue Library, so it can never hold a cluster.
 */
const UNSERVED_LIFE_AREAS = new Set(["SELF_KNOWLEDGE"]);

export const lifeAreaOptions = () =>
  knownLifeAreas()
    .filter((area) => !UNSERVED_LIFE_AREAS.has(area.key))
    .map((area) => ({ value: area.key, label: area.title }));

/** Clusters requested per list load: aris-ai's maximum page. */
export const CLUSTER_LIST_LIMIT = 200;

/**
 * The list footer. aris-ai's `count` is the page length, not a total, so a full
 * page means older clusters may exist beyond it.
 */
export const clusterCountLabel = (count, limit = CLUSTER_LIST_LIMIT) => {
  const n = Number(count) || 0;
  if (limit && n >= limit) {
    return `Showing the ${n} most recently seen clusters — narrow the filters to reach older ones`;
  }
  return `${n} cluster${n === 1 ? "" : "s"} · newest seen first`;
};

export const WINDOW_OPTIONS = [
  { value: "7", label: "Last 7 days" },
  { value: "14", label: "Last 14 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
];

/** ISO-8601 for `days` ago; `null` for "any time" / a non-number. */
export const sinceForDays = (days, now = new Date()) => {
  const n = Number(days);
  if (!days || days === "all" || !Number.isFinite(n) || n <= 0) return null;
  return new Date(now.getTime() - n * 24 * 60 * 60 * 1000).toISOString();
};

/** The row's headline: topic, else situation family, else its key. */
export const clusterTitle = (row) =>
  row?.topic || row?.situation_family || row?.cluster_key || row?.cluster_id || "—";

/** `{a: 3, b: 1}` → "a 3 · b 1", largest first. */
export const formatMix = (mix) =>
  Object.entries(mix || {})
    .filter(([, n]) => Number(n) > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([key, n]) => `${key.replace(/_/g, " ")} ${n}`)
    .join(" · ");

export const formatTrend = (trend) => {
  if (!trend) return "—";
  const last = Number(trend.last_window) || 0;
  const prior = Number(trend.prior_window) || 0;
  return `${last} vs ${prior} (${trend.window_days || 7}d)`;
};

/**
 * Nearest canonical Dialogues: the row's `nearest_ids`, enriched with the best
 * score, every "why insufficient" reason the sample signals recorded, and how
 * often the candidate was eligible / refused by a gate / of unknown eligibility.
 * Eligibility is per member, so it is counted, never merged into one verdict: a
 * Dialogue eligible for one member and gated for another is not "eligible".
 */
export const nearestDialogues = (cluster) => {
  const byId = new Map();
  const touch = (dialogueId) => {
    if (!dialogueId) return null;
    if (!byId.has(dialogueId)) {
      byId.set(dialogueId, {
        id: dialogueId,
        score: null,
        eligibility: { eligible: 0, refused: 0, unknown: 0 },
        reasons: [],
      });
    }
    return byId.get(dialogueId);
  };
  (cluster?.nearest_ids || []).forEach(touch);
  (cluster?.sample_signals || []).forEach((signal) => {
    (signal?.nearest || []).forEach((n) => {
      const entry = touch(n?.research_dialogue_id);
      if (!entry) return;
      const score = Number(n.score);
      if (Number.isFinite(score) && (entry.score == null || score > entry.score)) {
        entry.score = score;
      }
      if (n.eligible === true) entry.eligibility.eligible += 1;
      else if (n.eligible === false) entry.eligibility.refused += 1;
      else entry.eligibility.unknown += 1;
      const why = String(n.why_insufficient || "").trim();
      if (why && !entry.reasons.includes(why)) entry.reasons.push(why);
    });
  });
  return Array.from(byId.values());
};

/**
 * Badges for one candidate's eligibility counts. A candidate no sample signal
 * describes shows a single "Eligibility unknown".
 */
export const eligibilityBadges = (eligibility) => {
  const { eligible = 0, refused = 0, unknown = 0 } = eligibility || {};
  const times = (n) => (eligible + refused + unknown > 1 ? ` ×${n}` : "");
  const badges = [];
  if (eligible > 0) {
    badges.push({ key: "eligible", label: `Eligible${times(eligible)}`, variant: "success" });
  }
  if (refused > 0) {
    badges.push({
      key: "refused",
      label: `Refused by gate${times(refused)}`,
      variant: "warning",
    });
  }
  if (unknown > 0 || badges.length === 0) {
    badges.push({
      key: "unknown",
      label: `Eligibility unknown${unknown > 0 ? times(unknown) : ""}`,
      variant: "outline",
    });
  }
  return badges;
};

/** Traceability as ordered `{stage, label, ref, by, at}` steps (known stages first). */
export const traceabilitySteps = (traceability) => {
  const entries = Object.entries(traceability || {});
  const rank = (stage) => {
    const i = TRACE_STAGES.findIndex((s) => s.value === stage);
    return i === -1 ? TRACE_STAGES.length : i;
  };
  return entries
    .sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]))
    .map(([stage, value]) => {
      const detail =
        value && typeof value === "object" ? value : { ref: value == null ? null : String(value) };
      return {
        stage,
        label: traceStageLabel(stage),
        ref: detail.ref ?? null,
        by: detail.by ?? null,
        at: detail.at ?? null,
      };
    });
};

/** GET /clusters params from the page's filter state ("all" → omitted). */
export const buildClusterParams = ({ status, area, windowDays, limit }, now) => {
  const params = {};
  if (status && status !== "all") params.status = status;
  if (area && area !== "all") params.area = area;
  const since = sinceForDays(windowDays, now);
  if (since) params.since = since;
  if (limit) params.limit = limit;
  return params;
};
